import type { VisibilityState } from "@tanstack/react-table";

import type { ScreenerFilter } from "@/schemas/screener.schema";

export type ScreenerScope = "stocks" | "etf";

export interface ScreenerViewPreset {
  id: string;
  label: string;
  columns: string[];
}

export const SCREENER_FIXED_COLUMNS = ["company"] as const;

export const SCREENER_STOCKS_DEFAULT_ORDER: string[] = [
  "company",
  "sector",
  "market_cap_usd",
  "latest_price",
  "daily_change_percent",
  "trailing_pe",
  "dividend_yield",
  "return_on_equity",
  "forward_pe",
  "peg_ratio",
  "price_to_book",
  "latest_price_to_sales",
  "latest_price_to_earnings",
  "latest_price_to_fcf",
  "revenue_growth",
  "earnings_quarterly_growth",
  "gross_margins",
  "operating_margins",
  "profit_margins",
  "return_on_assets",
  "current_ratio",
  "quick_ratio",
  "payout_ratio",
  "beta",
  "short_ratio",
  "average_volume",
  "float_shares",
  "held_percent_insiders",
  "held_percent_institutions",
  "fifty_two_week_high",
  "fifty_two_week_low",
  "fifty_day_average",
  "two_hundred_day_average",
];

export const SCREENER_ETF_DEFAULT_ORDER: string[] = [
  "company",
  "category",
  "net_assets",
  "latest_price",
  "daily_change_percent",
  "yield",
  "expense_ratio",
  "ytd_return",
  "three_year_avg_return",
  "five_year_avg_return",
  "beta_3year",
  "market_cap_usd",
  "fund_family",
  "dividend_yield",
  "average_volume",
];

const STOCKS_DEFAULT_VISIBLE = new Set([
  "sector",
  "market_cap_usd",
  "latest_price",
  "daily_change_percent",
  "trailing_pe",
  "dividend_yield",
  "return_on_equity",
]);

const ETF_DEFAULT_VISIBLE = new Set([
  "category",
  "net_assets",
  "latest_price",
  "daily_change_percent",
  "yield",
  "expense_ratio",
]);

export const SCREENER_VIEW_PRESETS: Record<
  ScreenerScope,
  ScreenerViewPreset[]
> = {
  stocks: [
    {
      id: "general",
      label: "General",
      columns: [
        "company",
        "sector",
        "market_cap_usd",
        "latest_price",
        "daily_change_percent",
        "trailing_pe",
        "dividend_yield",
        "return_on_equity",
      ],
    },
    {
      id: "valuation",
      label: "Valuation",
      columns: [
        "company",
        "market_cap_usd",
        "trailing_pe",
        "forward_pe",
        "peg_ratio",
        "price_to_book",
        "latest_price_to_sales",
        "latest_price_to_earnings",
        "latest_price_to_fcf",
      ],
    },
    {
      id: "growth",
      label: "Growth",
      columns: [
        "company",
        "market_cap_usd",
        "revenue_growth",
        "earnings_quarterly_growth",
        "latest_price",
        "daily_change_percent",
      ],
    },
    {
      id: "quality",
      label: "Quality",
      columns: [
        "company",
        "return_on_equity",
        "return_on_assets",
        "gross_margins",
        "operating_margins",
        "profit_margins",
        "current_ratio",
        "quick_ratio",
      ],
    },
    {
      id: "income",
      label: "Income",
      columns: [
        "company",
        "dividend_yield",
        "payout_ratio",
        "market_cap_usd",
        "latest_price",
      ],
    },
  ],
  etf: [
    {
      id: "general",
      label: "General",
      columns: [
        "company",
        "category",
        "net_assets",
        "latest_price",
        "daily_change_percent",
        "yield",
        "expense_ratio",
      ],
    },
    {
      id: "valuation",
      label: "Valuation",
      columns: [
        "company",
        "net_assets",
        "market_cap_usd",
        "yield",
        "expense_ratio",
      ],
    },
    {
      id: "growth",
      label: "Growth",
      columns: [
        "company",
        "category",
        "ytd_return",
        "three_year_avg_return",
        "five_year_avg_return",
      ],
    },
    {
      id: "quality",
      label: "Quality",
      columns: [
        "company",
        "category",
        "expense_ratio",
        "beta_3year",
        "average_volume",
      ],
    },
    {
      id: "income",
      label: "Income",
      columns: [
        "company",
        "yield",
        "dividend_yield",
        "expense_ratio",
        "net_assets",
      ],
    },
  ],
};

export function getScreenerDefaultOrder(scope: ScreenerScope): string[] {
  return scope === "etf"
    ? SCREENER_ETF_DEFAULT_ORDER
    : SCREENER_STOCKS_DEFAULT_ORDER;
}

export function getScreenerDefaultVisibility(
  scope: ScreenerScope,
): VisibilityState {
  const visible =
    scope === "etf" ? ETF_DEFAULT_VISIBLE : STOCKS_DEFAULT_VISIBLE;
  const out: VisibilityState = {};
  for (const column of getScreenerDefaultOrder(scope)) {
    if (SCREENER_FIXED_COLUMNS.includes(column as "company")) {
      continue;
    }
    out[column] = visible.has(column);
  }
  return out;
}

export function normalizeScreenerOrder(
  scope: ScreenerScope,
  order: string[],
): string[] {
  const defaults = getScreenerDefaultOrder(scope);
  const allowed = new Set(defaults);
  const seen = new Set<string>();
  const result = ["company"];

  for (const id of order) {
    const normalizedId = normalizeScreenerColumnId(id);
    if (!allowed.has(normalizedId) || normalizedId === "company" || seen.has(normalizedId)) continue;
    seen.add(normalizedId);
    result.push(normalizedId);
  }
  for (const id of defaults) {
    if (id === "company" || seen.has(id)) continue;
    result.push(id);
  }
  return result;
}

export function normalizeScreenerVisibility(
  scope: ScreenerScope,
  visibility: VisibilityState | null | undefined,
): VisibilityState {
  const defaults = getScreenerDefaultOrder(scope);
  const allowed = new Set(defaults);
  const out = getScreenerDefaultVisibility(scope);

  if (!visibility) {
    return out;
  }

  for (const [key, value] of Object.entries(visibility)) {
    const normalizedKey = normalizeScreenerColumnId(key);
    if (!allowed.has(normalizedKey) || normalizedKey === "company") continue;
    out[normalizedKey] = !!value;
  }
  return out;
}

export function getScreenerDefaultSort(scope: ScreenerScope): {
  sortBy: string;
  sortOrder: "asc" | "desc";
} {
  if (scope === "etf") {
    return { sortBy: "net_assets", sortOrder: "desc" };
  }
  return { sortBy: "market_cap_usd", sortOrder: "desc" };
}

export function normalizeScreenerColumnId(id: string): string {
  return id === "market_cap" ? "market_cap_usd" : id;
}

export function sanitizePersistedScreenerFilters(
  filter: ScreenerFilter,
): Record<string, unknown> {
  const rest = { ...filter } as Record<string, unknown>;
  delete rest.types;
  delete rest.limit;
  delete rest.offset;
  delete rest.sort_by;
  delete rest.sort_order;
  delete rest.search;
  delete rest.group_by;
  return Object.fromEntries(
    Object.entries(rest).filter(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "string") return value !== "";
      return value !== undefined && value !== null;
    }),
  );
}
